/**
 * Per-broker override for the small print in the auth-screen footer
 * (root welcome, /trade/welcome, /trade/login, /trade/signup).
 *
 * WHY THIS EXISTS
 * The default line is a real-money derivatives disclaimer. It is correct for
 * every brokerage broker on this platform, but actively wrong for a broker
 * whose whole proposition is that no real money exists (gama market: virtual
 * funds only, no deposits, no withdrawals). Leaving it there contradicts that
 * broker's own marketing site AND its Play Store listing — an app whose store
 * page says "100% virtual, zero real money" while the app itself warns about
 * "substantial risk of loss" is exactly the kind of inconsistency a store
 * reviewer flags.
 *
 * SCOPING
 * One shared `web` container serves every broker's domain (see
 * nginx/includes/proxy-rules.conf), so a plain per-broker value would leak to
 * all of them. The hostname match at render time is what actually scopes it —
 * same mechanism as privacyPolicyUrlForHost() / accountDeletionUrlForHost() /
 * appVersionOverrideForHost(). Unset or no match => DEFAULT_APP_TAGLINE, i.e.
 * every other broker is byte-identical to before.
 *
 * FORMAT — `host|text` pairs, separated by SEMICOLONS:
 *   gama.example|Some sentence, with commas.;other.example|Another one.
 *
 * The three sibling variables separate their pairs with commas. This one
 * deliberately does not: its value is free prose, and prose contains commas.
 * Splitting on the first `|` only (rather than a plain split) means the text
 * may also contain `|` without corrupting the host boundary.
 *
 * .env HAZARD: `;` and `|` are both shell metacharacters, and deploy tooling
 * sources .env as bash (`set -a; . ./.env; set +a` in init-ssl.sh). The value
 * MUST stay double-quoted in every .env or it breaks unrelated deploy steps —
 * an unquoted `|` took out stage's SSL init once already.
 */

/** What every broker has always shown, and keeps showing unless overridden. */
export const DEFAULT_APP_TAGLINE =
  "Professional trading platform for futures & options. " +
  "Trading in derivatives involves substantial risk of loss. " +
  "Please read all related documents carefully before trading."

export function appTaglineForHost(host: string): string {
  const raw = process.env.NEXT_PUBLIC_APP_TAGLINE_OVERRIDES || ""
  for (const entry of raw.split(";")) {
    const sep = entry.indexOf("|")
    if (sep === -1) continue
    const pairHost = entry.slice(0, sep).trim()
    const pairText = entry.slice(sep + 1).trim()
    if (pairHost && pairText && pairHost.toLowerCase() === host.toLowerCase()) {
      return pairText
    }
  }
  return DEFAULT_APP_TAGLINE
}
